
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { getSessionToken } from '../auth/sessionToken';
import { useAuth } from '../auth/AuthContext';

const NotificationContext = createContext();
const NOTIFICATION_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
    const { isAuthenticated, authReady } = useAuth();
    const [socket, setSocket] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    // We store the current fetch params to know if we are in "all" or "unread" mode for pagination
    const currentFetchParams = useRef({ is_read: null });

    const reconnectTimeoutRef = useRef(null);
    const reconnectDelayRef = useRef(NOTIFICATION_REFRESH_INTERVAL_MS);
    const isMountedRef = useRef(true);
    const lastFetchCountTime = useRef(0);

    const socketRef = useRef(null);

    // Refs to expose current state to fetchNotifications without adding them as dependencies
    const notificationsRef = useRef(notifications);
    const loadingRef = useRef(loading);
    const hasMoreRef = useRef(hasMore);

    // Keep refs synced with state
    useEffect(() => { notificationsRef.current = notifications; }, [notifications]);
    useEffect(() => { loadingRef.current = loading; }, [loading]);
    useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

    // Helper for retrying
    const fetchWithRetry = async (url, options, retries = 0, backoff = 1000) => {
        try {
            const response = await fetch(url, options);
            if (!response.ok) {
                // Don't retry on 4xx errors except 429
                if (response.status >= 400 && response.status < 500 && response.status !== 429) {
                    return response;
                }
                if (retries > 0) throw new Error(`Request failed with status ${response.status}`);
                return response;
            }
            return response;
        } catch (e) {
            
            if (retries > 0) {
                await new Promise(r => setTimeout(r, backoff));
                return fetchWithRetry(url, options, retries - 1, backoff * 1.5);
            }
            throw e;
        }
    };

    // Initial fetch or pagination fetch
    // Stable identity - no dependencies
    const fetchNotifications = useCallback(async ({ reset = false, is_read = null } = {}) => {
        if (!isAuthenticated) return;
        const token = await getSessionToken();
        if (!token) return;

        if (reset) {
            setLoading(true);
            setNotifications([]);
            setHasMore(true);
            currentFetchParams.current = { is_read };
        } else {
            // Use Refs for checks to avoid dependency on state
            if (loadingRef.current || !hasMoreRef.current) return;
            setLoading(true);
        }

        try {
            const limit = 20;
            const offset = reset ? 0 : notificationsRef.current.length;

            const params = new URLSearchParams({
                limit: limit,
                offset: offset
            });

            const filterValue = reset ? is_read : currentFetchParams.current.is_read;
            if (filterValue !== null && filterValue !== undefined) {
                params.append('is_read', filterValue);
            }

            const response = await fetchWithRetry(`${API_ENDPOINTS.NOTIFICATIONS.LIST}?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response && response.ok) {
                const data = await response.json();
                const newNotes = data.notifications || [];

                if (newNotes.length < limit) {
                    setHasMore(false);
                }

                setNotifications(prev => {
                    if (reset) return newNotes;

                    const existingIds = new Set(prev.map(n => n.id));
                    const uniqueNew = newNotes.filter(n => !existingIds.has(n.id));
                    return [...prev, ...uniqueNew];
                });
            } else {
                // If final retry failed or 4xx error
                console.error('Failed to fetch notifications after retries or bad status');
                if (reset) setHasMore(false); // Stop infinite loading
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
            if (reset) setHasMore(false);
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [isAuthenticated]);

    const fetchUnreadCount = useCallback(async (force = false) => {
        if (!isAuthenticated) return;
        const now = Date.now();
        // Poll at most once every 10 minutes unless explicitly refreshed by the user.
        if (!force && now - lastFetchCountTime.current < NOTIFICATION_REFRESH_INTERVAL_MS) {
            return;
        }
        lastFetchCountTime.current = now;

        const token = await getSessionToken();
        if (!token) return;

        try {
            const response = await fetchWithRetry(API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response && response.ok) {
                const data = await response.json();
                setUnreadCount(data.unread_count || 0);
            }
        } catch (error) {
            console.error('Failed to fetch unread count:', error);
            // Fallback: don't update state
        }
    }, [isAuthenticated]);

    const markAsRead = async (notificationId) => {
        if (!isAuthenticated) return;
        const token = await getSessionToken();
        if (!token) return;

        try {
            // Optimistic update
            setNotifications(prev => prev.map(n =>
                n.id === notificationId ? { ...n, is_read: true } : n
            ));

            // Decrement unread count locally
            setUnreadCount(prev => Math.max(0, prev - 1));

            const response = await fetch(`${API_ENDPOINTS.NOTIFICATIONS.MARK_READ}${notificationId}/read`, {
                method: 'POST', // or PATCH
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                console.error('Failed to mark as read on server');
                // Revert count if needed? usually simpler to just re-fetch count later
                fetchUnreadCount();
            }
        } catch (error) {
            console.error('Error marking as read:', error);
            fetchUnreadCount();
        }
    };

    const markAllAsRead = async () => {
        if (!isAuthenticated) return;
        const token = await getSessionToken();
        if (!token) return;

        try {
            // Optimistic update
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);

            await fetch(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
        } catch (error) {
            console.error('Error marking all as read:', error);
            fetchUnreadCount();
        }
    };

    const clearNotifications = () => {
        setNotifications([]);
    };

    const connect = async () => {
        if (!isAuthenticated) return;
        const token = await getSessionToken();

        if (socketRef.current) {
            if (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CONNECTING) {
                return;
            }
        }

        if (!token) {
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
                setSocket(null);
                setIsConnected(false);
            }
            return;
        }

        const wsUrl = `${API_ENDPOINTS.NOTIFICATIONS.WS}?token=${token}`;
        const ws = new WebSocket(wsUrl);
        socketRef.current = ws;

        let pingInterval;

        ws.onopen = () => {
            
            setIsConnected(true);
            setSocket(ws);

            // Only reset reconnect delay after connection is stable for 5s
            // This prevents rapid reconnect loops if connection drops immediately
            setTimeout(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    reconnectDelayRef.current = NOTIFICATION_REFRESH_INTERVAL_MS;
                }
            }, 5000);

            // Refresh count on connect/reconnect
            fetchUnreadCount();

            pingInterval = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    try {
                        ws.send(JSON.stringify({ type: 'ping' }));
                    } catch (e) { console.error("Ping failed", e); }
                }
            }, 30000);
        };

        ws.onmessage = (event) => {
            try {
                const packet = JSON.parse(event.data);

                if (packet.type === 'system' && packet.message === 'connected') {
                    
                    return;
                }

                let notificationContent = packet;
                if (packet.type === 'notification' && packet.data) {
                    notificationContent = packet.data;
                }

                setNotifications(prev => {
                    if (notificationContent.id && prev.some(n => n.id === notificationContent.id)) {
                        return prev;
                    }
                    // Prepend new notification
                    return [notificationContent, ...prev];
                });

                // Increment unread count for new notification
                if (packet.type === 'notification') {
                    setUnreadCount(prev => prev + 1);
                }

            } catch (error) {
                console.error('Error parsing notification:', error);
            }
        };

        ws.onclose = async (event) => {
            clearInterval(pingInterval);

            if (socketRef.current === ws) {
                setIsConnected(false);
                setSocket(null);
                socketRef.current = null;

                if (!isMountedRef.current) return;

                const currentToken = await getSessionToken();

                // If token exists and close wasn't normal, try to reconnect with backoff
                if (currentToken && event.code !== 1000 && event.code !== 1001) {
                    

                    reconnectTimeoutRef.current = setTimeout(() => {
                        if (isMountedRef.current) connect();
                    }, reconnectDelayRef.current);

                    reconnectDelayRef.current = NOTIFICATION_REFRESH_INTERVAL_MS;
                }
            }
        };

        ws.onerror = () => {
            try { ws.close(); } catch (e) { }
        };
    };

    useEffect(() => {
        isMountedRef.current = true;

        if (!authReady || !isAuthenticated) {
            if (socketRef.current) socketRef.current.close();
            socketRef.current = null;
            setSocket(null);
            setIsConnected(false);
            setNotifications([]);
            setUnreadCount(0);
            return () => { isMountedRef.current = false; };
        }

        // Initial fetch: Load All
        // We comment this out because SellerNotifications component will trigger fetch on mount if it's the view
        // OR we leave it but ensure dedupe.
        // Actually, for global context it's good to fetch on mount.
        // But if user immediately goes to SellerNotifications, it might double fetch.
        // Let's leave it as is, dedupe logic handles it.
        fetchNotifications({ reset: true });
        fetchUnreadCount();

        connect();

        const handleAuthChange = async () => {
            const token = await getSessionToken();
            if (token) {
                if (isMountedRef.current) {
                    fetchNotifications({ reset: true });
                    fetchUnreadCount();
                    connect();
                }
            } else {
                if (socketRef.current) {
                    socketRef.current.close();
                    socketRef.current = null;
                }
                setIsConnected(false);
                setSocket(null);
                setNotifications([]);
                setUnreadCount(0);
            }
        };

        window.addEventListener('user-login', handleAuthChange);
        window.addEventListener('app-logout', handleAuthChange);
        window.addEventListener('auth-user-updated', handleAuthChange);

        return () => {
            isMountedRef.current = false;
            window.removeEventListener('user-login', handleAuthChange);
            window.removeEventListener('app-logout', handleAuthChange);
            window.removeEventListener('auth-user-updated', handleAuthChange);

            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
            if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        };
    }, [authReady, isAuthenticated, fetchNotifications, fetchUnreadCount]);

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            isConnected,
            loading,
            hasMore,
            fetchNotifications,
            fetchUnreadCount,
            clearNotifications,
            markAsRead,
            markAllAsRead,
            socket
        }}>
            {children}
        </NotificationContext.Provider>
    );
};
